import React from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faPen, faPlus, faRefresh, faTrash } from '@fortawesome/free-solid-svg-icons';
import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import Table from '../../Components/Table/Table';
import { ManagementDataService, problemMessage, rowsFromResponse } from '../../Services/ManagementDataService';
import { connectionInitial, hasProvider, managementConfigs } from './managementConfigs';
import './ManagementPage.scss';

const initialFor = (fields, row) => Object.fromEntries(fields.map(field => [
  field.name,
  row?.[field.name] ?? field.defaultValue ?? (field.type === 'switch' ? false : '')
]));

const selectRows = rows => rows.filter(row => row.isActive !== false);

const optionName = (row, type) => type === 'company'
  ? `${row.name}${row.code ? ` (${row.code})` : ''}`
  : `${row.name}${row.code ? ` (${row.code})` : ''}`;

function DynamicField({ field, value, onChange, lookups, isExisting }) {
  if (field.type === 'hidden') return null;
  if (field.type === 'switch') {
    return <Form.Check className="mb-3" type="switch" id={field.name} label={field.label} checked={Boolean(value)} onChange={e => onChange(e.target.checked)} />;
  }

  let options;
  if (field.type === 'company') options = lookups.companies;
  if (field.type === 'institution') options = lookups.institutions;
  if (field.type === 'bank-service') options = lookups.bankServices;
  if (options) {
    return (
      <Form.Group className="mb-3" controlId={field.name}>
        <Form.Label>{field.label}</Form.Label>
        <Form.Select value={value ?? ''} onChange={e => onChange(e.target.value ? Number(e.target.value) : '')} required={field.required}>
          <option value="">{field.optional ? 'None' : `Select ${field.label.toLowerCase()}...`}</option>
          {options.map(row => {
            const id = field.type === 'company' ? row.companyId : field.type === 'institution' ? row.financialInstitutionId : row.bankServiceId;
            const label = field.type === 'bank-service'
              ? (row.serviceName || row.name || row.description || `Service ${id}`)
              : optionName(row, field.type);
            return <option value={id} key={id}>{label}</option>;
          })}
        </Form.Select>
      </Form.Group>
    );
  }

  const required = field.required || (field.newRequired && !isExisting);
  return (
    <Form.Group className="mb-3" controlId={field.name}>
      <Form.Label>{field.label}</Form.Label>
      {field.type === 'textarea' ? (
        <Form.Control as="textarea" rows={3} value={value ?? ''} onChange={e => onChange(e.target.value)} required={required} />
      ) : (
        <>
          <Form.Control
            type={field.type === 'password' ? 'password' : field.type === 'url' ? 'url' : 'text'}
            list={field.type === 'creatable-select' ? `${field.name}-options` : undefined}
            value={value ?? ''}
            onChange={e => onChange(e.target.value)}
            required={required}
            pattern={field.pattern}
            maxLength={field.maxLength}
            inputMode={field.inputMode}
            autoComplete={field.type === 'password' ? 'new-password' : undefined}
          />
          {field.type === 'creatable-select' && (
            <datalist id={`${field.name}-options`}>{field.options.map(option => <option value={option} key={option} />)}</datalist>
          )}
        </>
      )}
      {field.editHint && isExisting && <Form.Text className="text-muted">{field.editHint}</Form.Text>}
    </Form.Group>
  );
}

function EditModal({ show, config, row, lookups, onHide, onSave }) {
  const [form, setForm] = React.useState(() => initialFor(config.fields, row));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => { setForm(initialFor(config.fields, row)); setError(''); }, [config.fields, row, show]);

  const submit = async event => {
    event.preventDefault(); setSaving(true); setError('');
    try {
      const body = { ...form };
      config.fields.filter(field => field.numeric).forEach(field => {
        body[field.name] = body[field.name] === '' ? null : Number(body[field.name]);
      });
      await onSave(body);
    } catch (err) { setError(problemMessage(err)); } finally { setSaving(false); }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title>{row ? 'Edit' : 'Create'} {config.singular}</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>{config.fields.map(field => (
            <Col md={field.type === 'textarea' ? 12 : 6} key={field.name}>
              <DynamicField field={field} value={form[field.name]} onChange={value => setForm(current => ({ ...current, [field.name]: value }))} lookups={lookups} />
            </Col>
          ))}</Row>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button></Modal.Footer>
      </Form>
    </Modal>
  );
}

function ConnectionModal({ data, lookups, onHide, onSave }) {
  const { row, connection } = data || {};
  const initial = React.useMemo(() => data ? connectionInitial(row, connection) : {}, [data, row, connection]);
  const [form, setForm] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const isExisting = Boolean(form[connection?.existingId]);

  React.useEffect(() => { setForm(initial); setError(''); }, [initial]);
  if (!data) return null;

  const submit = async event => {
    event.preventDefault(); setSaving(true); setError('');
    try {
      const body = { ...form };
      const existingId = body[connection.existingId];
      if (existingId === '' || existingId === null || existingId === undefined) {
        delete body[connection.existingId];
      } else {
        body[connection.existingId] = Number(existingId);
      }
      connection.fields.filter(field => field.numeric).forEach(field => {
        body[field.name] = body[field.name] === '' ? null : Number(body[field.name]);
      });
      connection.fields.filter(field => field.type === 'password').forEach(field => {
        if (isExisting && !body[field.name]) delete body[field.name];
      });
      await onSave(body);
    } catch (err) { setError(problemMessage(err)); } finally { setSaving(false); }
  };

  return (
    <Modal show centered size="lg" onHide={onHide}>
      <Form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title>{connection.label}: {row.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>{connection.fields.map(field => <Col md={6} key={field.name}><DynamicField field={field} value={form[field.name]} onChange={value => setForm(current => ({ ...current, [field.name]: value }))} lookups={lookups} isExisting={isExisting} /></Col>)}</Row>
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save connection'}</Button></Modal.Footer>
      </Form>
    </Modal>
  );
}

const importErrors = response => {
  const results = Array.isArray(response) ? response : response?.results;
  return response?.errors ?? response?.rowErrors ?? response?.failedRows ?? results?.filter(item => item.success === false || item.error || item.title) ?? [];
};

function ImportModal({ show, config, companies, onHide, onImport }) {
  const [file, setFile] = React.useState(null);
  const [companyId, setCompanyId] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [result, setResult] = React.useState(null);

  React.useEffect(() => { if (show) { setFile(null); setCompanyId(''); setError(''); setResult(null); } }, [show]);
  const submit = async event => {
    event.preventDefault(); setUploading(true); setError(''); setResult(null);
    try { setResult(await onImport(file, companyId)); } catch (err) { setError(problemMessage(err, 'The file could not be imported.')); } finally { setUploading(false); }
  };
  const errors = importErrors(result);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title>Import {config.title}</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {result && <Alert variant={errors.length ? 'warning' : 'success'}>{errors.length ? 'Import completed with row errors.' : (result.title || result.message || 'Import completed successfully.')}</Alert>}
          <p className="text-muted small">{config.importHelp}</p>
          <Form.Group className="mb-3" controlId="management-import-file">
            <Form.Label>CSV or XLSX file</Form.Label>
            <Form.Control type="file" accept=".csv,.xlsx" required onChange={event => setFile(event.target.files?.[0] || null)} />
          </Form.Group>
          {config.importCompany && <Form.Group className="mb-3" controlId="management-import-company"><Form.Label>Company (optional)</Form.Label><Form.Select value={companyId} onChange={event => setCompanyId(event.target.value)}><option value="">Resolve company from each row</option>{companies.map(company => <option key={company.companyId} value={company.companyId}>{optionName(company, 'company')}</option>)}</Form.Select></Form.Group>}
          {errors.length > 0 && <div className="table-responsive management-import-errors"><table className="table table-sm"><thead><tr><th>Row</th><th>Error</th></tr></thead><tbody>{errors.map((item, index) => <tr key={`${item.rowNumber ?? item.row ?? index}-${index}`}><td>{item.rowNumber ?? item.row ?? index + 1}</td><td>{item.title ?? item.message ?? item.error ?? JSON.stringify(item)}</td></tr>)}</tbody></table></div>}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button><Button type="submit" disabled={!file || uploading}>{uploading ? 'Importing...' : 'Import'}</Button></Modal.Footer>
      </Form>
    </Modal>
  );
}

function ManagementPageContent({ resource }) {
  const config = managementConfigs[resource];
  const service = ManagementDataService[config.service];
  const { instance, accounts, inProgress } = useMsal();
  const [rows, setRows] = React.useState([]);
  const [lookups, setLookups] = React.useState({ companies: [], institutions: [], bankServices: [] });
  const [query, setQuery] = React.useState({ search: '', includeInactive: false, companyId: '', financialInstitutionId: '' });
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [edit, setEdit] = React.useState(undefined);
  const [deleting, setDeleting] = React.useState(null);
  const [connection, setConnection] = React.useState(null);
  const [showImport, setShowImport] = React.useState(false);

  const token = React.useCallback(async () => {
    const request = { ...loginRequest, account: accounts[0] };
    return (await instance.acquireTokenSilent(request)).accessToken;
  }, [accounts, instance]);

  const load = React.useCallback(async () => {
    if (inProgress !== 'none' || !accounts.length) return;
    setLoading(true); setLoadError('');
    try {
      const accessToken = await token();
      const params = { search: query.search, includeInactive: query.includeInactive };
      if (config.filters?.includes('companyId')) params.companyId = query.companyId;
      if (config.filters?.includes('financialInstitutionId')) params.financialInstitutionId = query.financialInstitutionId;
      setRows(rowsFromResponse(await service.list(params, accessToken)));
    } catch (err) { setLoadError(problemMessage(err, `Could not load ${config.title.toLowerCase()}.`)); } finally { setLoading(false); }
  }, [accounts.length, config, inProgress, query, service, token]);

  React.useEffect(() => { const timer = setTimeout(load, 300); return () => clearTimeout(timer); }, [load]);
  React.useEffect(() => {
    if (inProgress !== 'none' || !accounts.length) return;
    (async () => {
      try {
        const accessToken = await token();
        const [companies, institutions, bankServices] = await Promise.all([
          ManagementDataService.companies.list({ includeInactive: false }, accessToken),
          ManagementDataService.institutions.list({ includeInactive: false }, accessToken),
          config.connections?.some(item => item.fields.some(field => field.type === 'bank-service')) ? ManagementDataService.bankServices(accessToken) : []
        ]);
        setLookups({ companies: selectRows(rowsFromResponse(companies)), institutions: selectRows(rowsFromResponse(institutions)), bankServices: rowsFromResponse(bankServices) });
      } catch (err) { /* Main list still remains usable if an optional lookup fails. */ }
    })();
  }, [accounts.length, config.connections, inProgress, token]);

  const save = async body => {
    const accessToken = await token();
    if (edit) await service.update(edit[config.id], body, accessToken); else await service.create(body, accessToken);
    setEdit(undefined); setSuccess(`${config.singular[0].toUpperCase()}${config.singular.slice(1)} saved successfully.`); await load();
  };
  const remove = async () => {
    try { await service.remove(deleting[config.id], await token()); setDeleting(null); setSuccess('Record deleted successfully.'); await load(); }
    catch (err) { setDeleting(null); setLoadError(problemMessage(err, 'The record could not be deleted.')); }
  };
  const saveConnection = async body => {
    await service.configure(connection.row[config.id], connection.connection.provider, body, await token());
    setConnection(null); setSuccess('Connection saved successfully.'); await load();
  };
  const upload = async (file, companyId) => {
    const response = await service.importFile(file, companyId, await token());
    await load(); return response;
  };

  const columns = React.useMemo(() => [
    ...config.columns.map(([header, key]) => ({
      header, accessorKey: key,
      cell: ({ getValue }) => config.maskCard && key === 'lastFourDigits' ? <span className="text-nowrap">•••• {String(getValue() ?? '').padStart(4, '0')}</span> : getValue()
    })),
    ...(config.badges ? [{ header: 'Connections', id: 'connections', enableSorting: false, cell: ({ row }) => <div className="d-flex gap-1">{config.badges.map(code => <Badge bg={hasProvider(row.original, code) ? 'success' : 'secondary'} key={code}>{code}</Badge>)}</div> }] : []),
    { header: 'Status', accessorKey: 'isActive', cell: ({ getValue }) => <Badge bg={getValue() ? 'success' : 'secondary'}>{getValue() ? 'Active' : 'Inactive'}</Badge> },
    { header: 'Actions', id: 'actions', enableSorting: false, cell: ({ row }) => <div className="management-actions"><Button size="sm" variant="outline-secondary" title="Edit" onClick={() => setEdit(row.original)}><FontAwesomeIcon icon={faPen} /></Button>{config.connections?.map(item => <Button size="sm" variant="outline-primary" key={item.provider} onClick={() => setConnection({ row: row.original, connection: item })}>{item.label}</Button>)}<Button size="sm" variant="outline-danger" title="Delete" onClick={() => setDeleting(row.original)}><FontAwesomeIcon icon={faTrash} /></Button></div> }
  ], [config]);

  return <div className="management-page">
    {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
    {loadError && <Alert variant="danger" dismissible onClose={() => setLoadError('')}>{loadError}</Alert>}
    <div className="management-toolbar">
      <Form.Control aria-label="Search" placeholder={`Search ${config.title.toLowerCase()}...`} value={query.search} onChange={event => setQuery(current => ({ ...current, search: event.target.value }))} />
      {config.filters?.includes('companyId') && <Form.Select aria-label="Filter by company" value={query.companyId} onChange={event => setQuery(current => ({ ...current, companyId: event.target.value }))}><option value="">All companies</option>{lookups.companies.map(row => <option value={row.companyId} key={row.companyId}>{row.name}</option>)}</Form.Select>}
      {config.filters?.includes('financialInstitutionId') && <Form.Select aria-label="Filter by financial institution" value={query.financialInstitutionId} onChange={event => setQuery(current => ({ ...current, financialInstitutionId: event.target.value }))}><option value="">All institutions</option>{lookups.institutions.map(row => <option value={row.financialInstitutionId} key={row.financialInstitutionId}>{row.name}</option>)}</Form.Select>}
      <Form.Check type="switch" id={`${resource}-inactive`} label="Include inactive" checked={query.includeInactive} onChange={event => setQuery(current => ({ ...current, includeInactive: event.target.checked }))} />
      <Button variant="outline-primary" title="Refresh" onClick={load}><FontAwesomeIcon icon={faRefresh} /></Button>
      <Button variant="outline-primary" onClick={() => setShowImport(true)}><FontAwesomeIcon icon={faFileImport} className="me-1" />Import</Button>
      <Button onClick={() => setEdit(null)}><FontAwesomeIcon icon={faPlus} className="me-1" />New</Button>
    </div>
    {loading ? <div className="management-state"><Spinner animation="border" /><span>Loading...</span></div> : rows.length ? <Table columns={columns} data={rows} tableClass="table-hover" headerClass="table-light" containerClass="table-responsive" /> : <Alert variant="light" className="management-state">No records found.</Alert>}
    {edit !== undefined && <EditModal show config={config} row={edit} lookups={lookups} onHide={() => setEdit(undefined)} onSave={save} />}
    <ConnectionModal data={connection} lookups={lookups} onHide={() => setConnection(null)} onSave={saveConnection} />
    <ImportModal show={showImport} config={config} companies={lookups.companies} onHide={() => setShowImport(false)} onImport={upload} />
    {deleting && <ConfirmationModal show title={`Delete ${config.singular}`} content={`Are you sure you want to delete “${deleting.name || deleting.cardholderName}”?`} onHide={() => setDeleting(null)} onCancelClick={() => setDeleting(null)} onAcceptClick={remove} />}
  </div>;
}

export default function ManagementPage({ resource }) {
  const config = managementConfigs[resource];
  return <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}><PageLayout2 title={config.title}><ManagementPageContent resource={resource} /></PageLayout2></MsalAuthenticationTemplate>;
}
